import { Flex, TableRowMenuCell, Tooltip, theme } from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { TABLE_ROW_MENU_WIDTH } from '@/utils';
import { CommentOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { type ComponentProps, type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { isNotUsePinpoint } from '../../utils/pinpoint';

type PinpointType = {
  pinpoint?: {
    onPinpoint: () => void;
    project: z.infer<typeof ProjectModel> | undefined;
  };
};
type TableRowMenuCellProps = ComponentProps<typeof TableRowMenuCell>;

type PinpointTableRowMenuCellProps = PinpointType & TableRowMenuCellProps;

const PinpointTableRowMenuCell: FC<PinpointTableRowMenuCellProps> = ({
  pinpoint,
  ...props
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const outlinedStyle = useMemo(
    () => ({
      color: token.colorLink,
      fontSize: 21,
    }),
    [token],
  );

  const { notUsePinpoint } = isNotUsePinpoint(pinpoint?.project);

  const showPinpoint = useMemo(() => {
    return !notUsePinpoint && pinpoint;
  }, [notUsePinpoint, pinpoint]);

  return (
    <TableRowMenuCell
      {...props}
      additonalItem={
        showPinpoint ? (
          <Tooltip title={t('comment.add')}>
            <CommentOutlined
              style={outlinedStyle}
              onClick={pinpoint?.onPinpoint}
            />
          </Tooltip>
        ) : null
      }
    />
  );
};

export const pinpointTableActionColumn = <T,>({
  showWithoutPinpoint,
  render,
}: {
  showWithoutPinpoint: boolean;
  render: (record: T) => PinpointTableRowMenuCellProps;
}): ColumnsType<T>[number] => {
  const defaultPrams = {
    title: '',
    key: 'actions',
    fixed: 'right',
    width: TABLE_ROW_MENU_WIDTH,
  } as const;

  return showWithoutPinpoint
    ? {
        ...defaultPrams,
        render: (_, record) => (
          <PublishedProjectMutableGuard>
            <Flex justify="end">
              <PinpointTableRowMenuCell {...render(record)} />
            </Flex>
          </PublishedProjectMutableGuard>
        ),
      }
    : {
        ...defaultPrams,
        render: (record) => {
          const { pinpoint } = render(record);
          return (
            <PublishedProjectMutableGuard>
              <Flex justify="end">
                <PinpointTableRowMenuCell pinpoint={pinpoint} />
              </Flex>
            </PublishedProjectMutableGuard>
          );
        },
      };
};
